import { cloneDeep, get, orderBy } from "lodash";

import {
  AnalyticsResourceType,
  AnalyzeResourceResponseBodyType,
  DatasetId,
  ExperienceActionType,
  ExperienceDto,
  ExperienceListDto,
  MetricArrayType,
  MetricType,
  VariationDto,
} from "@intelligems/ig-types";
import { getFirstStartedAtTs, getLastEndedAtTs } from "@intelligems/ig-utils";
import { UseQueryResult } from "@tanstack/react-query";

import type {
  DashboardGraphConfigs,
  GraphConfig,
} from "../../domains/Dashboard/Graphs/Utils/graphConfigs";
import {
  alwaysOnIntervalGraphConfig,
  alwaysOnTotals,
  discountsGraphsConfig,
  GraphConfigGroup,
  GraphType,
  impactGraphConfig,
  ordersGraphsConfig,
  overviewGraphsConfig,
  personalizationMainMetricsGraphsConfig,
  productGroupsGraphsConfig,
  profitGraphsConfig,
  revenueGraphsConfig,
  shippingGraphsConfig,
  singlePersonalizationTotal,
  statSigGraphsConfig,
  subscriptionsGraphConfig,
  timeSeriesGraphsConfig,
  visitorsGraphsConfig,
} from "../../domains/Dashboard/Graphs/Utils/graphConfigs";
import { getStackedDataValue } from "../../domains/Dashboard/Graphs/Utils/graphHelpers";
import type { IExperienceDashboardFilters } from "../../store/types/dashboardFilters";
import type {
  BarMetricData,
  HistogramMetricData,
  LineMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../types/analytics";
import { ExperienceFe } from "../../types/schemas/experiences";
import { VariationFe } from "../../types/schemas/variations";
import {
  genericAscSort,
  genericBucketAscSort,
  genericDescSort,
  genericSort,
  genericSortByOrder,
  sortByBucket,
  sortByOrder,
  sortByStatus,
} from "../../utils/sortings";
import { useConfiguration } from "../configurations/use-configuration";
import type {
  DashboardPageName,
  FilterDialogButtons,
} from "../types/dashboards";

// returns oldest active experience, else return newest ended experience,
// else return newest paused experience else return null
export const experienceFinder = (
  experiences: ExperienceDto[]
): ExperienceDto | null => {
  if (experiences !== undefined) {
    // find all active experiences
    const getActiveExperiences = experiences.filter(
      (experience: ExperienceDto) => experience.status === "started"
    );
    // find all ended experiences
    const getEndedExperiences = experiences.filter(
      (experience: ExperienceDto) => experience.status === "ended"
    );
    const getPausedExperiences = experiences.filter(
      (experience: ExperienceDto) => experience.status === "paused"
    );

    if (getActiveExperiences.length > 0) {
      return getActiveExperiences.reduce(
        (min: ExperienceDto, experience: ExperienceDto) =>
          experience.startedAtTs! < min.startedAtTs! ? experience : min,
        getActiveExperiences[0]
      );
    } else if (getEndedExperiences.length > 0) {
      return getEndedExperiences.reduce(
        (max: ExperienceDto, experience: ExperienceDto) =>
          experience.endedAtTs! > max.endedAtTs! ? experience : max,
        getEndedExperiences[0]
      );
    } else if (getPausedExperiences.length > 0) {
      return getPausedExperiences.reduce(
        (max: ExperienceDto, experience: ExperienceDto) =>
          experience.pausedAtTs! > max.pausedAtTs! ? experience : max,
        getPausedExperiences[0]
      );
    } else return null; // all experiences are pending
  } else return null; // no experiences at all
};

export const campaignFinder = (campaigns: ExperienceDto[]) => {
  if (campaigns.length > 0) {
    const getActiveCampaigns = campaigns.filter(
      (campaign) => campaign.startedAtTs !== null && campaign.endedAtTs === null
    );
    const getInactiveCampaigns = campaigns.filter(
      (campaign) => campaign.startedAtTs !== null && campaign.endedAtTs !== null
    );

    if (getActiveCampaigns.length > 0) {
      return getActiveCampaigns.reduce(
        (min, campaign) =>
          campaign.startedAtTs! < min.startedAtTs! ? campaign : min,
        getActiveCampaigns[0]
      );
    } else if (getInactiveCampaigns.length > 0) {
      return getInactiveCampaigns.reduce(
        (max, campaign) =>
          campaign.endedAtTs! > max.startedAtTs! ? campaign : max,
        getInactiveCampaigns[0]
      );
    } else {
      return null;
    }
  } else {
    return null;
  }
};

// export const getInitialstartTime = (
//   resourceType: AnalyticsResourceType,
//   currentResource: ExperienceDto | CampaignWithIdType
// ) => {
//   if (resourceType === "campaigns") {
//     if ((currentResource as CampaignWithIdType).enabledAtTs) {
//       return (currentResource as CampaignWithIdType).enabledAtTs!;
//     } else {
//       return null;
//     }
//   } else {
//     if ((currentResource as ExperienceDto).startedAtTs) {
//       return (currentResource as ExperienceDto).startedAtTs!;
//     } else {
//       return null;
//     }
//   }
// };
//
// export const getInitialendTime = (
//   resourceType: AnalyticsResourceType,
//   currentResource: ExperienceDto | CampaignWithIdType
// ) => {
//   if (resourceType === "campaigns") {
//     if ((currentResource as CampaignWithIdType).disabledAtTs) {
//       return (currentResource as CampaignWithIdType).disabledAtTs!;
//     } else {
//       return null;
//     }
//   } else {
//     if ((currentResource as ExperienceDto).endedAtTs) {
//       return (currentResource as ExperienceDto).endedAtTs!;
//     } else {
//       return null;
//     }
//   }
// };

export const getNewestEndedTest = (
  experiences: ExperienceDto[]
): ExperienceDto | undefined => {
  const endedExperiences = experiences.filter(
    (experience: ExperienceDto) => experience.status === "ended"
  );

  if (endedExperiences.length > 0) {
    return endedExperiences.reduce(
      (max: ExperienceDto, experience: ExperienceDto) =>
        experience.endedAtTs! > max.endedAtTs! ? experience : max,
      endedExperiences[0]
    );
  } else return undefined;
};

export const getOldestStartedTest = (experiences: ExperienceDto[]) => {
  const activeExperiences = getRunningExperiences(experiences);
  if (activeExperiences.length > 0) {
    return activeExperiences.reduce(
      (max: ExperienceDto, experience: ExperienceDto) =>
        experience.startedAtTs! < max.startedAtTs! ? experience : max,
      activeExperiences[0]
    );
  } else return undefined;
};

// return experiences with status Started or Ended
export const activeExperiencesFilter = (
  experienceList: ExperienceDto[]
): ExperienceListDto[] | ExperienceDto[] => {
  return sortByStatus(
    experienceList.filter(
      (experience: ExperienceDto) => experience.status !== "pending"
    )
  );
};

export const getRunningExperiences = (
  experiences: ExperienceDto[] | ExperienceListDto[],
  hasImpactData?: boolean
) => {
  return experiences.filter((experience: ExperienceDto) =>
    hasImpactData
      ? experience.status === "started" &&
        experience?.experienceAnalysis?.impact
      : experience.status === "started"
  );
};

export const getHistogramData = (
  data: HistogramMetricData[],
  groupName: string
) => {
  const groupData: HistogramMetricData[] = [];
  const dataCopy = cloneDeep(data);
  if (groupName === "all") {
    dataCopy.forEach((data: HistogramMetricData) => {
      const doesBucketExist = groupData.findIndex(
        (data1) => data1.bucket === data.bucket
      );

      if (doesBucketExist === -1) {
        groupData.push(data);
      } else {
        groupData[doesBucketExist] = {
          title: data.title,
          name: data.name,
          metricLabels: data.metricLabels,
          bucket: data.bucket,
          bucketCount: (data.bucketCount +=
            groupData[doesBucketExist].bucketCount),
        };
      }
    });
  } else {
    dataCopy.forEach((data: HistogramMetricData) => {
      if (data.name === groupName) {
        groupData.push(data);
      }
    });
  }
  return groupData.sort(sortByBucket);
};

export const getFilterMethod = (
  data: BarMetricData[],
  filterMethod: string | undefined
) => {
  const groupData: BarMetricData[] = [];
  const dataCopy = cloneDeep(data);

  dataCopy.forEach((data: BarMetricData) => {
    if (data.filterMethod === filterMethod) {
      groupData.push(data);
    }
  });

  return groupData;
};

export const getGraphConfig = (
  pageName: DashboardPageName
): DashboardGraphConfigs => {
  switch (pageName) {
    case "overview":
      return overviewGraphsConfig;
    case "profit":
      return profitGraphsConfig;
    case "revenue":
      return revenueGraphsConfig;
    case "shipping":
      return shippingGraphsConfig;
    case "visitors":
      return visitorsGraphsConfig;
    case "orders":
      return ordersGraphsConfig;
    case "discounts":
      return discountsGraphsConfig;
    case "productGroups":
      return productGroupsGraphsConfig;
    case "impact":
      return impactGraphConfig;
    case "personalizations":
      return personalizationMainMetricsGraphsConfig;
    case "singlePersonalizationTotal":
      return singlePersonalizationTotal;
    case "timeSeries":
      return timeSeriesGraphsConfig;
    case "statSig":
      return statSigGraphsConfig;
    case "subscriptions":
      return subscriptionsGraphConfig;
    case "alwaysOn":
      return alwaysOnIntervalGraphConfig;
    case "alwaysOnCumulative":
      return alwaysOnTotals;
    default:
      return overviewGraphsConfig;
  }
};

export const getSubPage = (url: string, obj: object): string | undefined => {
  for (const value of Object.values(obj)) {
    if (url.includes(value)) {
      return value;
    }
  }
  return undefined;
};

export const getFilterTime = (
  resource: ExperienceFe,
  dashboardFilters: IExperienceDashboardFilters,
  dashKey: keyof IExperienceDashboardFilters,
  resourceKey: "startedAtTs" | "endedAtTs" | "pausedAtTs"
): number | null => {
  if (dashboardFilters[dashKey]) {
    return dashboardFilters[dashKey] as number | null;
  } else if (resource[resourceKey]) {
    return resource[resourceKey] as number | null;
  } else return null;
};

export const getInitialStartFilterTime = (
  resource: ExperienceFe,
  dashboardFilters: IExperienceDashboardFilters
): number | null => {
  if (dashboardFilters.startTime) {
    return dashboardFilters.startTime as number | null;
  } else {
    return getFirstStartedAtTs(resource.experienceActions);
  }
};

export const getLastEndFilterTime = (
  resource: ExperienceFe,
  dashboardFilters: IExperienceDashboardFilters
): number | null => {
  if (dashboardFilters.endTime) {
    return dashboardFilters.endTime as number | null;
  } else {
    return getLastEndedAtTs(resource.experienceActions);
  }
};

export const filterDashboardData = (
  dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType>[],
  datasetId: DatasetId | DatasetId[],
  config?: GraphConfig
): MetricArrayType | undefined => {
  if (!dashboardData) {
    return undefined;
  }

  const filteredData =
    dashboardData.find((data) => {
      if (data.data) {
        if (data.data.datasetId === datasetId) {
          return true;
        }
      }
      return false;
    })?.data || [];

  if (config?.isLossMetric) {
    if (config.isLossMetric === "profit") {
      return get(filteredData, "loss.profit", undefined);
    } else {
      return get(filteredData, "loss.revenue", undefined);
    }
  }

  return get(filteredData, "metrics", undefined);
};

export const getTotalTestVisitors = (
  dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType>[]
) => {
  const revDataset = dashboardData.find(
    (metric) => metric.data?.datasetId === "overview"
  );

  if (revDataset) {
    const metrics = revDataset.data?.metrics.map(
      (metric) => metric["VISITORS"]
    );
    if (metrics) {
      return metrics.reduce(
        (prev, curr) => (prev as number) + (curr as number),
        0
      ) as number;
    }
  }
};

//#region -- Graph Data Formatters ---
const histogramFormatter = (
  dashboardData: MetricArrayType,
  config: GraphConfig,
  variations?: VariationFe[] | VariationDto[]
): HistogramMetricData[] => {
  return dashboardData.map((data) => {
    return {
      title: config.title[0],
      name:
        variations?.find((v) => v.id === data["VARIATION_ID"])?.name ||
        ("" as string),
      bucket: data[config.histogramBucket!] as number,
      bucketCount: data[config.metrics[0]] as number,
      metricLabels: config.metrics,
    };
  });
};

const stackedAreaFormatter = (
  dashboardData: MetricArrayType,
  config: GraphConfig,
  locale?: string
): StackedAreaMetricData[] => {
  const formattedData: StackedAreaMetricData[] = [];

  const uniqueFilterValues = dashboardData
    .map((metric) => metric[config.filterMetric!])
    .filter((val, id, array) => array.indexOf(val) === id)
    .sort();

  uniqueFilterValues.forEach((value) => {
    formattedData.push({ xAxisKey: value as string });
  });

  formattedData.forEach((data: StackedAreaMetricData, index: number) => {
    const groupsByFilterMetric = dashboardData.filter(
      (group: MetricType) => group[config.filterMetric!] === data.xAxisKey
    );
    groupsByFilterMetric.forEach((groupData: MetricType) => {
      if (config.metrics.length === 1) {
        Object.assign(formattedData[index], {
          title: config.title,
          metricLabels: config.metrics,
          [groupData["VARIATION_ID"]]: groupData[config.metrics[0]],
        });
      } else {
        // sitewide
        Object.assign(formattedData[index], {
          title: config.title,
          metricLabels: config.metrics,
          [config.metrics[0]]: groupData[config.metrics[0]],
          [config.metrics[1]]: groupData[config.metrics[1]],
        });
      }
    });
  });

  return formattedData.map((data: StackedAreaMetricData) => {
    return {
      ...data,
      xAxisKey: new Date(data.xAxisKey).toLocaleDateString(locale || "en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  });
};

const stackedBarFormatter = (
  dashboardData: MetricArrayType,
  config: GraphConfig,
  variations: VariationFe[] | VariationDto[]
): StackedBarMetricData[] => {
  if (config.metrics.length > 1 && config.stackedBarLegendTitles) {
    return dashboardData
      .map((data: MetricType) => {
        const testGroupMetrics = variations.find(
          (group) => group.id === data["VARIATION_ID"]
        );
        return {
          title: config.title[0],
          name:
            variations.find((v) => v.id === data["VARIATION_ID"])?.name || "",
          order: testGroupMetrics ? testGroupMetrics?.order : 0,
          metricLabels: config.metrics,
          ids: [
            config.stackedBarLegendTitles![0],
            config.stackedBarLegendTitles![1],
          ],
          [config.stackedBarLegendTitles![0]]: data[config.metrics[0]] || 0,
          [config.stackedBarLegendTitles![1]]: data[config.metrics[1]] || 0,
        };
      })
      .sort(sortByOrder);
  } else if (config.filterMetric) {
    const formattedData: StackedBarMetricData[] = [];

    const sortedUniqueCategories = sortByMetricFilter(dashboardData, config);
    variations.forEach((group) => {
      formattedData.push({ id: group.id, order: group.order });
    });

    formattedData.forEach((data: StackedBarMetricData, index: number) => {
      const dataByTestGroup = dashboardData.filter(
        (group: MetricType) => group["VARIATION_ID"] === data.id
      );
      const categoriesByGroup = dataByTestGroup.map(
        (group) => group[config.filterMetric!]
      );
      dataByTestGroup.forEach((groupData: MetricType) => {
        Object.assign(formattedData[index], {
          title: config.title,
          metricLabels: config.metrics,
          ids: categoriesByGroup,
          allIds: sortedUniqueCategories,
          [groupData[config.filterMetric!]]: groupData[config.metrics[0]!],
        });
      });
    });

    return formattedData.sort(genericSortByOrder);
  } else {
    // should never be used, will delete when I ensure we will always have filterMetric
    return variations
      .map((group) => {
        const testGroupMetrics = dashboardData.filter(
          (groupMetric: MetricType) => groupMetric.label === group.id
        );

        let dataValue1: string | number | undefined = undefined;
        let dataValue2: string | number | undefined = undefined;

        if (testGroupMetrics.length > 0) {
          testGroupMetrics.forEach((metric) => {
            if (metric["CUSTOMER_TYPE"] === "Returning Customer") {
              dataValue1 = metric[config.metrics[0]];
            } else if (metric["CUSTOMER_TYPE"] === "New Customer") {
              dataValue2 = metric[config.metrics[0]];
            } else return;
          });
        }

        return {
          title: config.title[0],
          name: group.name,
          value1: getStackedDataValue(config, dataValue1, dataValue2),
          value2: getStackedDataValue(config, dataValue2, dataValue1),
          order: group.order,
          metricLabels: config.metrics,
        };
      })
      .sort(sortByOrder);
  }
};

const barFormatter = (
  dashboardData: MetricArrayType,
  config: GraphConfig,
  variations: VariationFe[] | VariationDto[],
  metricFilterIndex?: number
): BarMetricData[] => {
  return dashboardData
    .map((data: MetricType) => {
      const testGroup = variations.find(
        (group) => group.id === data["VARIATION_ID"]
      );
      const filterMethod = data[config.filterMetric!];
      let dataKey =
        config.metrics[
          config.hasMetricFilterIndex && metricFilterIndex
            ? metricFilterIndex
            : 0
        ];

      if (
        [
          "conversion_best",
          "conversion_beat_control",
          "average_order_value_best",
          "average_order_value_beat_control",
          "revenue_per_visitor_best",
          "revenue_per_visitor_beat_control",
          "profit_per_visitor_best",
          "profit_per_visitor_beat_control",
        ].includes(dataKey)
      ) {
        dataKey = `STAT_SIG_PROBABILITIES.${dataKey}`;
      }
      return {
        title: config.title[0],
        id:
          variations.find((v) => v.id === data["VARIATION_ID"])?.id ||
          ("" as string),
        value: get(data, dataKey, 0),
        order: testGroup && testGroup.order,
        metricLabels: config.metrics,
        filterMethod: filterMethod ? (filterMethod as string) : undefined,
      };
    })
    .sort(genericSortByOrder);
};

const lineFormatter = (
  dashboardData: MetricArrayType,
  resourceType: AnalyticsResourceType,
  config: GraphConfig,
  locale?: string,
  metricFilterIndex?: number
): LineMetricData[] => {
  if (
    resourceType === "campaigns" ||
    (config.datasetId === "alwaysOnInterval" && config.metrics.length === 1)
  ) {
    return cloneDeep(dashboardData)
      .sort((a, b) => genericSort(a, b, config.filterMetric!))
      .map((data) => {
        return {
          xAxisKey: new Date(data[config.filterMetric!]).toLocaleDateString(
            "en-US",
            {
              month: "short",
              day: "numeric",
            }
          ),
          value: data[config.metrics[0]],
          metricLabels: [config.metrics[0]],
        };
      });
  } else if (
    config.datasetId === "cumulativeRevenueProfitOverTime" ||
    (config.datasetId === "alwaysOnInterval" && config.metrics.length > 1)
  ) {
    const formattedData: LineMetricData[] = [];
    const uniqueFilterValues = dashboardData
      .map((metric) => metric[config.filterMetric!])
      .filter((val, id, array) => array.indexOf(val) === id)
      .sort();

    uniqueFilterValues.forEach((value) => {
      formattedData.push({ xAxisKey: value as string });
    });

    formattedData.forEach((data: LineMetricData, index: number) => {
      const groupsByFilterMetric = dashboardData.filter(
        (group: MetricType) => group[config.filterMetric!] === data.xAxisKey
      );
      groupsByFilterMetric.forEach((groupData: MetricType) => {
        if (config.metrics.length === 1) {
          Object.assign(formattedData[index], {
            title: config.title,
            metricLabels: config.metrics,
            [groupData["VARIATION_ID"]]: groupData[config.metrics[0]],
          });
        } else {
          Object.assign(formattedData[index], {
            title: config.title,
            metricLabels: config.metrics,
            [config.metrics[0]]: groupData[config.metrics[0]],
            [config.metrics[1]]: groupData[config.metrics[1]],
          });
        }
      });
    });

    return formattedData.map((data: LineMetricData) => {
      return {
        ...data,
        xAxisKey: new Date(data.xAxisKey!).toLocaleDateString(
          locale || "en-US",
          {
            month: "short",
            day: "numeric",
            hour: config.datasetId.includes("alwaysOn") ? undefined : "numeric",
          }
        ),
      };
    });
  } else if (config.isLossMetric) {
    const formattedData: LineMetricData[] = [];
    const uniqueFilterValues = dashboardData
      .map((metric) => metric[config.filterMetric!])
      .filter((val, id, array) => array.indexOf(val) === id)
      .sort();

    uniqueFilterValues.forEach((value) => {
      formattedData.push({ xAxisKey: value as string });
    });

    formattedData.forEach((data: LineMetricData, index: number) => {
      const groupsByFilterMetric = dashboardData.filter(
        (group: MetricType) => group[config.filterMetric!] === data.xAxisKey
      );
      groupsByFilterMetric.forEach((groupData: MetricType) => {
        Object.assign(formattedData[index], {
          title: config.title,
          metricLabels: config.metrics,
          [groupData["VARIATION_ID"]]:
            groupData[config.metrics[metricFilterIndex || 0]],
        });
      });
    });
    return formattedData;
  } else {
    const graphData: LineMetricData[] = [
      { xAxisKey: "Add To Cart Rate", metricLabels: ["ATC_RATE"] },
      {
        xAxisKey: "Begin Checkout Rate",
        metricLabels: ["BEGIN_CHECKOUT_RATE"],
      },
      { xAxisKey: "Conversion Rate", metricLabels: ["CVR"] },
    ];

    dashboardData.forEach((data) => {
      graphData.splice(0, 1, {
        ...graphData[0],
        [data["VARIATION_ID"]]: data["ATC_RATE"],
      });
      graphData.splice(1, 1, {
        ...graphData[1],
        [data["VARIATION_ID"]]: data["BEGIN_CHECKOUT_RATE"],
      });
      graphData.splice(2, 1, {
        ...graphData[2],
        [data["VARIATION_ID"]]: data["CVR"],
      });
    });
    return graphData;
  }
};
//#endregion -- Graph Data Formatters ---

export const orderFiltersByValue = (data: BarMetricData[]) => {
  const allTogether: any[] = [];
  const filterMethodList = data
    .map((metric: BarMetricData) => metric.filterMethod)
    .filter((val, id, array) => array.indexOf(val) === id);

  filterMethodList.forEach((value) => {
    allTogether.push({ filterMethod: value as string, value: 0 });
  });

  allTogether.forEach((data1: BarMetricData, index: number) => {
    const groupsByRate = data.filter(
      (group: BarMetricData) => group.filterMethod === data1.filterMethod
    );
    groupsByRate.forEach((groupData: BarMetricData) => {
      Object.assign(allTogether[index], {
        value: (allTogether[index].value += groupData.value),
      });
    });
  });

  return allTogether.sort(genericDescSort);
};

export const getDefaultGroupGraphFilterValue = (
  data: BarMetricData[] | LineMetricData[] | undefined,
  configGroup: GraphConfigGroup,
  variations?: VariationFe[]
) => {
  if (data && data.length > 0) {
    if (configGroup.groupFilterMetric === "VARIATION_ID" && variations) {
      const groupValues = {};
      variations.forEach((group) => {
        groupValues[group.id] = data[0][group.id];
      });

      return Object.keys(groupValues).reduce((prev, curr) =>
        groupValues[prev] > groupValues[curr] ? prev : curr
      );
    } else {
      const mostOrderRate = orderFiltersByValue(data as BarMetricData[]);
      const firstDefinedMethod = mostOrderRate.findIndex(
        (val) => val.filterMethod !== undefined || val.filterMethod! == null
      );
      if (firstDefinedMethod !== -1) {
        return mostOrderRate[firstDefinedMethod].filterMethod;
      }
    }
  } else return "";
};

export const sortByMetricFilter = (
  dashboardData: MetricArrayType,
  config: GraphConfig
) => {
  const filterMethodList = dashboardData
    .map((metric: MetricType) => metric[config.filterMetric!])
    .filter((val, id, array) => array.indexOf(val) === id);

  const newObj: any[] = [];
  if (config.filterMetric) {
    filterMethodList.forEach((val) => {
      const allOccurrences = dashboardData.filter(
        (data) => data[config.filterMetric!] === val
      );
      const filterMetricTotal = allOccurrences
        .map((data) => data[config.sortMetric!])
        .reduce((prev, curr) => (prev as number) + (curr as number), 0);

      newObj.push({ [config.filterMetric!]: val, value: filterMetricTotal });
    });

    return newObj
      .sort(
        (config.sortOrder === "ascending" &&
          config.title[0] === "% Orders by Unit Quantity") ||
          config.title[0] === "% Net Revenue by Order Unit Quantity"
          ? genericBucketAscSort
          : config.sortOrder === "ascending"
            ? genericAscSort
            : genericDescSort
      )
      .map((val) => val[config.filterMetric!]);
  } else {
    return filterMethodList;
  }
};

export const selectData = (
  resourceType: AnalyticsResourceType,
  dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType>[],
  config: GraphConfig,
  _resource?: ExperienceFe,
  locale?: string,
  metricFilterIndex?: number,
  variations?: VariationFe[] | VariationDto[]
):
  | BarMetricData[]
  | HistogramMetricData[]
  | StackedBarMetricData[]
  | StackedAreaMetricData[]
  | LineMetricData[]
  | undefined => {
  const filteredDashboardData = filterDashboardData(
    dashboardData,
    config.datasetId,
    config
  );
  if (!filteredDashboardData) {
    return undefined;
  }
  if (
    !_resource &&
    !variations &&
    (config.graphType === GraphType.StackedBar ||
      config.graphType === GraphType.ClusteredBar ||
      config.graphType === GraphType.BiaxialBar ||
      config.graphType === GraphType.Bar)
  ) {
    return undefined;
  }
  if (config.graphType === GraphType.Histogram) {
    return histogramFormatter(filteredDashboardData || [], config, variations);
  } else if (config.graphType === GraphType.Line) {
    return lineFormatter(
      filteredDashboardData || [],
      resourceType,
      config,
      locale,
      metricFilterIndex
    );
  } else if (config.graphType === GraphType.StackedArea) {
    return stackedAreaFormatter(filteredDashboardData || [], config, locale);
  } else if (
    config.graphType === GraphType.StackedBar ||
    config.graphType === GraphType.ClusteredBar ||
    config.graphType === GraphType.BiaxialBar
  ) {
    return stackedBarFormatter(
      filteredDashboardData || [],
      config,
      variations!
    );
  } else {
    return barFormatter(
      filteredDashboardData,
      config,
      variations!,
      metricFilterIndex
    );
  }
};

export const shouldShowFilter = (
  filter: FilterDialogButtons,
  pageName?: string
) => {
  if (
    (filter.config === true || filter.config === undefined) &&
    filter.showOnPage === "all"
  ) {
    return true;
  } else if (filter.config === true || filter.config === undefined) {
    return pageName === filter.showOnPage || pageName !== filter.dontShowOnPage;
  } else return false;
};

export const canReorderTestGroups = (config: GraphConfig): boolean => {
  return (
    !config.isImpactGraph &&
    config.graphType !== GraphType.StackedArea &&
    config.graphType !== GraphType.Histogram &&
    config.graphType !== GraphType.SyncedBar &&
    config.graphType !== GraphType.Line
  );
};

export const shouldShowGraphHeader = (
  shouldShowProfit: boolean,
  config: GraphConfig
): boolean => {
  return config.showProfitPlaceholder === true && !shouldShowProfit;
};

export const useGraphValueFormatter = () => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const returnFunc = (
    isAxisTick: boolean,
    value: number,
    config: Pick<GraphConfig, "formatter" | "dataTypes">,
    _index?: number
  ): string | undefined => {
    const index = _index ?? 0;
    if (storeCurrencyConfig && storeCurrencyConfig.value) {
      if (config.formatter) {
        return config.formatter(value, isAxisTick);
      } else {
        if (config.dataTypes[index] === "currency") {
          return currencyFormatter(
            value,
            isAxisTick,
            storeCurrencyConfig!.value!
          );
        } else if (config.dataTypes[index] === "percent") {
          return percentFormatter(
            value,
            isAxisTick,
            storeCurrencyConfig!.value!
          );
        } else if (config.dataTypes[index] === "percentTwoDecimal") {
          return percentTwoDecimalFormatter(
            value,
            isAxisTick,
            storeCurrencyConfig!.value!
          );
        } else if (config.dataTypes[index] === "numberNoDecimal") {
          return noDecimalNumberFormatter(
            value,
            isAxisTick,
            storeCurrencyConfig!.value!
          );
        } else if (config.dataTypes[index] === "currencyNoDecimal") {
          return noDecimalCurrencyFormatter(
            value,
            isAxisTick,
            storeCurrencyConfig!.value!
          );
        } else if (config.dataTypes[index] === "numberNoRounding") {
          return numberNoRoundingFormatter(
            value,
            isAxisTick,
            storeCurrencyConfig!.value!
          );
        } else {
          return numberFormatter(
            value,
            isAxisTick,
            storeCurrencyConfig!.value!
          );
        }
      }
    }
  };

  return (
    isAxisTick: boolean,
    value: number,
    config: Pick<GraphConfig, "formatter" | "dataTypes">,
    index?: number
  ) => returnFunc(isAxisTick, value, config, index);
};

export const calculateCogsWarning = (dashboardProfitData: MetricArrayType) => {
  const totalRevNoCogs = dashboardProfitData.reduce(
    (prev, curr) => prev + (curr["PRODUCT_REVENUE_WITHOUT_COGS"] as number),
    0
  );
  const totalRev = dashboardProfitData.reduce(
    (prev, curr) => prev + (curr["TOTAL_PRODUCT_REVENUE"] as number),
    0
  );

  return (totalRevNoCogs || 0) / (totalRev || 0);
};

//#region -- Number Formatters ---
export const currencyFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  try {
    return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
      style: "currency",
      currency: `${storeCurrency.code}`,
      notation: tick ? "compact" : undefined,
      compactDisplay: tick ? "short" : undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(value)
      .replace(/\D00(?=\D*$)/, "")}`; // determines which numbers have decimal places based on size
  } catch (e) {
    // invalid locale, default to USD
    return `${new Intl.NumberFormat(`en-US`, {
      style: "currency",
      currency: `${storeCurrency.code}`,
      notation: tick ? "compact" : undefined,
      compactDisplay: tick ? "short" : undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(value)
      .replace(/\D00(?=\D*$)/, "")}`;
  }
};

export const percentFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    maximumSignificantDigits: tick ? 2 : undefined,
    minimumSignificantDigits: tick ? 1 : undefined,
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(value * 100)}%`;
};

export const percentTwoDecimalFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    maximumSignificantDigits: 3,
    minimumSignificantDigits: 1,
    minimumFractionDigits: 1,
  }).format(value * 100)}%`;
};

export const numberFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,
    maximumSignificantDigits: tick ? 2 : 3,
    minimumSignificantDigits: tick ? 1 : 3,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;
};

export const numberNoRoundingFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,
  }).format(value)}`;
};

export const noDecimalCurrencyFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "currency",
    currency: `${storeCurrency.code}`,
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/\D00(?=\D*$)/, "")}`;
};

export const noDecimalNumberFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value)}`;
};

export const noPrefixCurrencyFormatter = (
  value: number,
  storeCurrency: { code: string; locale: string }
) => {
  return new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/\D00(?=\D*$)/, "");
};

//#endregion -- Number Formatters ---
